/**
 * One icon per workspace (WALLIYO-0134), from the approved design.
 *
 * The header carries the department's own mark beside its name. Direction B
 * kept exactly one icon — the rail of them was direction A, and it cost 94px of
 * chrome for the same information.
 *
 * Drawn rather than imported: a dependency for seven outlines is a dependency to
 * keep updated, and these never change without the design changing.
 */

import type { DepartmentId } from "@wallyo/contracts/workspaces";
import type { ReactNode } from "react";

/** The strokes of each workspace's mark, on a 24×24 grid. */
const MARKS: Partial<Record<DepartmentId, ReactNode>> = {
  // A line climbing out of two axes: the company's own numbers.
  executive: (
    <>
      <path d="M3 3v18h18" />
      <path d="m7 14 4-4 3 3 5-6" />
    </>
  ),
  // A speech bubble, because Support is a conversation that has gone wrong.
  support: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  // A shield.
  trust: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  // A card, with its stripe.
  finance: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
    </>
  ),
  // A wrench.
  product: <path d="M14.7 6.3a4 4 0 0 0 5 5l-9 9a2.8 2.8 0 0 1-4-4z" />,
  // A line going up and to the right, with its arrow.
  growth: (
    <>
      <path d="M3 17l6-6 4 4 7-7" />
      <path d="M14 8h6v6" />
    </>
  ),
  // A padlock: who may do what.
  administration: (
    <>
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  // A shopfront awning: the market itself.
  marketplace: (
    <>
      <path d="M3 9h18l-2-5H5L3 9z" />
      <path d="M5 9v11h14V9" />
    </>
  ),
  // People.
  people: (
    <>
      <path d="M16 20v-2a4 4 0 0 0-8 0v2" />
      <circle cx="12" cy="8" r="4" />
    </>
  ),
};

export function WorkspaceMark({ id, size = 17 }: { id: DepartmentId; size?: number }) {
  const mark = MARKS[id];
  if (!mark) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {mark}
    </svg>
  );
}

/** The chevron on the switcher, which the design draws rather than types. */
export function Chevron({ up = false }: { up?: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={up ? { transform: "rotate(180deg)" } : undefined}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
